import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Search } from "lucide-react";
import Dialog from "components/shared/Dialog";
import { Input } from "components/ui/input";
import { useVisible } from "utils/hooks/useVisible";

function FieldList({ label, values }: { label: string; values: string[] }) {
    const { t } = useTranslation();
    const { visible, open, close } = useVisible();
    const [search, setSearch] = useState("");

    const hasMore = values.length > 1;
    const filtered = values.slice(1).filter((v) => v.toLowerCase().includes(search.toLowerCase()));

    return (
        <>
            <div className="tw-flex tw-items-center tw-justify-between tw-text-sm tw-text-muted-foreground tw-mb-2 tw-border-b tw-py-2 last:tw-border-b-0 last:tw-mb-0 sm:[&:nth-last-child(2):nth-child(odd)]:tw-border-b-0 sm:[&:nth-last-child(2):nth-child(odd)]:tw-mb-0">
                <h4>{label}</h4>
                {values.length === 0 ? (
                    <span className="tw-italic">{t("not-set")}</span>
                ) : (
                    <div className="tw-flex tw-items-center tw-gap-2">
                        <span className="tw-text-neutral-900">{values[0]}</span>
                        {hasMore && (
                            <button
                                type="button"
                                onClick={() => open()}
                                className="tw-cursor-pointer tw-text-xs tw-text-muted-foreground tw-border tw-rounded-full tw-px-2 tw-py-0.5 hover:tw-bg-muted tw-transition-colors"
                            >
                                +{values.length - 1}
                            </button>
                        )}
                    </div>
                )}
            </div>

            {hasMore && (
                <Dialog
                    open={visible}
                    close={close}
                    title={label}
                    bodyClassName="tw-p-0 tw-flex tw-flex-col"
                >
                    <div className="tw-relative tw-px-4 tw-py-3">
                        <Search className="tw-absolute tw-left-7 tw-top-1/2 -tw-translate-y-1/2 tw-size-3.5 tw-text-muted-foreground tw-pointer-events-none" />
                        <Input
                            placeholder={t("search")}
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            className="tw-pl-7"
                        />
                    </div>
                    <ul className="tw-max-h-72 tw-overflow-y-auto tw-flex tw-flex-col tw-gap-1.5 tw-p-4">
                        {filtered.length === 0 ? (
                            <li className="tw-py-6 tw-text-sm tw-text-muted-foreground tw-text-center">
                                {t("no-results-found")}
                            </li>
                        ) : (
                            filtered.map((value) => (
                                <li
                                    key={value}
                                    className="tw-rounded-md tw-bg-muted/60 tw-px-3 tw-py-2 tw-text-sm tw-text-neutral-900"
                                >
                                    {value}
                                </li>
                            ))
                        )}
                    </ul>
                </Dialog>
            )}
        </>
    );
}

export default FieldList;
